import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Shared/contexts/AuthContext';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';

const VerifyEmail: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleResend = async (): Promise<void> => {
    setStatus('sending');
    try {
      await apiClient.resendVerificationEmail();
      setStatus('sent');
    } catch (err: unknown) {
      setErrorMessage(extractApiError(err));
      setStatus('error');
    }
  };

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
          <p className="text-gray-600">
            We sent a verification link to your email address. Click the link to activate your
            account before you can continue.
          </p>
        </div>

        {status === 'sent' && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-700">Verification email sent! Check your inbox.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{errorMessage}</p>
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={() => void handleResend()}
            disabled={status === 'sending' || status === 'sent'}
            className="w-full"
          >
            {status === 'sending'
              ? 'Sending...'
              : status === 'sent'
              ? 'Email sent'
              : 'Resend verification email'}
          </Button>

          <button
            onClick={handleLogout}
            className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Sign out and use a different account
          </button>
        </div>
      </Card>
    </div>
  );
};

export default VerifyEmail;
